/**
 * The tutorial screen.
 *
 * Everything here runs locally: the script, the engine, the whole game. There is no
 * server involved, no account, no match record and no network round trip per line of
 * dialogue — the 2020 version needed all four, plus a privileged bot account and a
 * `'tutorial'` formation threaded through the server to support it.
 *
 * The board is the same component the real game uses, driven by the same rules, so what
 * the tutorial teaches cannot drift from what the game does.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Move } from '@gok/engine';
import { threatenedKings } from '@gok/engine';
import { SCRIPT, TutorialRun, type TutorialView } from '@gok/tutorial';

import { type Selection, targetsFor } from '../game/selection.ts';
import { Board } from '../ui/Board.tsx';
import { Reserve } from '../ui/Reserve.tsx';
import { Button, Panel } from '../ui/primitives.tsx';
import { navigate, PLAY_HREF } from './router.ts';

export function Tutorial() {
  const [view, setView] = useState<TutorialView | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  const runRef = useRef<TutorialRun | null>(null);

  useEffect(() => {
    // A ref rather than state: the run is a long-lived object, not a value to diff.
    const run = new TutorialRun(SCRIPT, () => setView({ ...run.view }));
    runRef.current = run;
    setView({ ...run.view });
    return () => {
      run.dispose();
      runRef.current = null;
    };
  }, []);

  const play = useCallback((move: Move) => {
    runRef.current?.play(move);
    setSelection(null);
  }, []);

  const targets = useMemo(
    () => (view ? targetsFor(view.position, view.legalMoves, selection) : null),
    [view, selection],
  );

  if (!view || !targets) return null;

  const { position } = view;
  const waiting = view.awaiting !== undefined;

  return (
    <div className='flex h-full min-h-0 flex-col lg:flex-row'>
      {
        /* The board keeps a floor on a phone. Without it the transcript grows to fit its
          text and squeezes the board down to a thumbnail — which is the one thing a
          tutorial cannot afford, since every instruction is about what is on it. */
      }
      <div className='relative flex min-h-[38vh] flex-1 items-center justify-center p-2
          sm:p-3 lg:min-h-0'>
        <div className='h-full w-full'>
          <Board
            board={position.board}
            cells={position.cells}
            selection={selection}
            targets={targets}
            viewFrom={0}
            toMove={position.playerToMove}
            interactive={waiting}
            threatened={threatenedKings(position)}
            hint={view.hint}
            onSelect={setSelection}
            onPlay={play}
          />
        </div>
      </div>

      <aside className='flex max-h-[56vh] w-full shrink-0 flex-col gap-2 overflow-hidden border-t
          border-rule bg-ground p-2 lg:h-full lg:max-h-none lg:w-96 lg:gap-3 lg:border-t-0
          lg:border-l lg:p-3'>
        <Panel
          title='Tutorial'
          className='min-h-0 flex-1'
          actions={
            <span className='tabular font-mono text-[0.7rem] text-muted'>
              {view.progress.done}/{view.progress.total}
            </span>
          }
        >
          <Transcript view={view} />
        </Panel>

        {view.nudge && (
          <p
            className='rounded-md border border-alert bg-alert-soft px-3 py-2 text-sm text-alert'
            role='status'
          >
            {view.nudge}
          </p>
        )}

        <Reserve
          count={position.spawns[0]}
          player={0}
          selected={selection?.kind === 'reserve'}
          onPick={waiting
            ? () =>
              setSelection((current) =>
                current?.kind === 'reserve' ? null : { kind: 'reserve' }
              )
            : undefined}
        />

        <div className='flex flex-wrap gap-2'>
          {waiting && (
            <>
              <Button onClick={() => runRef.current?.showHint()} className='flex-1'>
                Show me
              </Button>
              <Button onClick={() => runRef.current?.resetTask()} className='flex-1'>
                Reset position
              </Button>
            </>
          )}
          {view.finished && (
            <>
              {
                /* The tutorial ends by handing you an opponent, not a lobby. Knowing the
                rules and having nobody to try them on is where a new player is lost. */
              }
              <Button
                tone='primary'
                onClick={() => navigate(PLAY_HREF)}
                className='flex-1'
              >
                Play the bot
              </Button>
              <Button onClick={() => navigate('#/')} className='flex-1'>
                Find a game
              </Button>
            </>
          )}
          <Button onClick={() => runRef.current?.restart()} className='flex-1'>
            Start over
          </Button>
        </div>
      </aside>
    </div>
  );
}

function Transcript({ view }: { view: TutorialView }) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [view.transcript.length]);

  return (
    <div className='min-h-24 flex-1 overflow-y-auto px-3 py-2 lg:min-h-40'>
      {view.transcript.map((line, index) => (
        <p
          key={line.id}
          className={`mb-2 text-sm last:mb-0 ${
            index === view.transcript.length - 1 ? 'text-ink' : 'text-muted'
          }`}
        >
          {line.text}
        </p>
      ))}
      <div ref={endRef} />
    </div>
  );
}
